import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useError } from './ErrorContext';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const { setError } = useError();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        console.log("UserContext: Initializing auth state");
        
        // First check for an existing session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.warn("Session error:", sessionError.message);
          setUser(null);
          setAuthChecked(true);
          return;
        }
        
        if (session) {
          console.log("Found existing session, getting user data");
          // If we have a session, get the user data
          const { data: { user }, error } = await supabase.auth.getUser();
          if (error) {
            console.warn("Error getting user:", error.message);
            
            // Try to refresh the session
            const { data, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.warn("Error refreshing session:", refreshError.message);
              setUser(null);
            } else if (data.session) {
              console.log("Session refreshed successfully");
              setUser(data.user);
            } else {
              setUser(null);
            }
          } else {
            console.log("User data retrieved successfully");
            setUser(user);
          }
        } else {
          console.log("No active session found");
          // If no session: try getUser() anyway - important for token redirects from Supabase
          try {
            const { data: { user }, error } = await supabase.auth.getUser();
            if (error) {
              // Don't throw here, just log the error
              console.warn("No active session and getUser failed:", error.message);
              setUser(null);
            } else if (user) {
              console.log("User found despite no session:", user);
              setUser(user);
            } else {
              console.log("No user found");
              setUser(null);
            }
          } catch (getUserError) {
            console.error("Error in getUser:", getUserError);
            setUser(null);
          }
        }
      } catch (err) {
        console.error("Auth error:", err);
        setUser(null);
        // Don't set error here as it would block the app from loading
        // We'll handle auth errors at the component level
      } finally {
        setAuthChecked(true);
      }
    };
    
    fetchUser();
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event);
      if (event === 'SIGNED_IN' && session?.user) {
        console.log("User signed in:", session.user);
        setUser(session.user);
      } else if (event === 'SIGNED_OUT') {
        console.log("User signed out");
        setUser(null);
      } else if (event === 'TOKEN_REFRESHED') {
        // When token is refreshed, update the user
        if (session?.user) {
          console.log("Token refreshed, updating user");
          setUser(session.user);
        }
      } else if (event === 'USER_UPDATED') {
        // When user is updated, update the user state
        if (session?.user) {
          console.log("User updated, updating state");
          setUser(session.user);
        }
      } else if (event === 'SESSION_EXPIRED') {
        console.log("Session expired");
        setUser(null);
        
        // Try to refresh the session
        const { data, error } = await supabase.auth.refreshSession();
        if (error) {
          console.warn("Error refreshing expired session:", error.message);
        } else if (data.session) {
          console.log("Session refreshed successfully after expiration");
          setUser(data.user);
        }
      }
    });
    
    return () => subscription.unsubscribe();
  }, []);

  return (
    <UserContext.Provider value={{ user, setUser, authChecked }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);